import { useCallback, useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { skipToken, useMutation, useSuspenseQuery } from '@apollo/client';
import { showFullDate, showShortsDateRange } from '@/utils/common';
import { DefaultDropdown } from '@/component/Dropdown';
import { DatePicker } from '@/component/DateRange';
import { Loader } from '@/components/base/Layout/Loader';
import { SearchListingDetailsPriceQuery, reservationCreate } from '@/query';
import { makeGuestsValidWithLimit } from '@/utils/guests';
import { pushCheckAvailability, pushBookingCheckoutStart } from '@/utils/gtm';
import {
  getFormatValue,
  getHasDiscountTitle,
  getTitleMoneyPerDayValue,
} from '../price_utils';
import { format, addDays } from 'date-fns';
import { getDateBegining } from './date_utils';
import { PaymentScheduleModal } from './PaymentScheduleModal';
import { GuestsWidget } from './GuestsWidget';
import { DatesWidget, useDatesWidget } from './DatesWidget';
import styles from './AvailabilityWidget.module.css';
import { useParams, useSearchParams } from 'next/navigation';
import { usePathname, useRouter } from '@/i18n/routing';
import { useAvailabilityPeriods } from '../availability_periods';
import { maxDate, minDate } from '../constants';
import { useUpdateQueryDates, useWatchDateQuery } from '../hooks';

export const AvailabilityWidget = ({
  priceStartDate,
  priceEndDate,
  daysAmount,
  details,
  hasDates,
  priceLoading,
  startTransition,
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const params = useParams();
  const searchParams = useSearchParams();
  const { withDatesWidget } = useDatesWidget();
  const [showError, setShowError] = useState(false);
  const [showPaymentSchedule, setPaymentSchedule] = useState(false);
  const [isDateDropdownVisible, setIsDateDropdownVisible] = useState(false);

  const {
    disabledDay,
    disabledDates,
    startValue,
    endValue,
    setValue,
    getMinimumDays,
    getDaysRequirement,
  } = useAvailabilityPeriods({
    calendar: details?.calendar,
    queryDates: {
      startDate: searchParams.get('startDate'),
      endDate: searchParams.get('endDate'),
    },
  });

  const {
    data: priceData,
    error: priceError,
    networkStatus,
  } = useSuspenseQuery(
    SearchListingDetailsPriceQuery,
    priceStartDate && priceEndDate
      ? {
          variables: {
            priceStartDate: format(new Date(priceStartDate), 'yyyy-MM-dd'),
            priceEndDate: format(new Date(priceEndDate), 'yyyy-MM-dd'),
            slug: params.slug,
          },
          errorPolicy: 'all',
        }
      : skipToken
  );
  const loading = networkStatus < 7;

  const price = priceData?.showListingPrice?.price;

  const onUpdateQueryDates = useUpdateQueryDates();

  const onChange = (selection) => {
    setShowError(false);
    const { startDate, endDate } = selection;
    setValue({ startValue: startDate, endValue: endDate });
  };

  const handleCloseDateModal = () => {
    !withDatesWidget && setIsDateDropdownVisible(false);
  };

  const dateRef = useDetectClickOutside({ onTriggered: handleCloseDateModal });

  useEffect(() => {
    if (!priceData?.showListingPrice?.price?.regular?.totalPrice) {
      setShowError(true);
    }
  }, [priceData?.showListingPrice?.price?.regular?.totalPrice]);

  useEffect(() => {
    if (priceStartDate && priceEndDate) {
      const minBeggining = getDateBegining(priceStartDate);

      if (minBeggining) {
        onUpdateQueryDates({
          startDate: minBeggining,
          endDate: getDateBegining(priceEndDate, true) || priceEndDate,
        });
      } else if (priceStartDate === priceEndDate) {
        onUpdateQueryDates({
          startDate: priceStartDate,
          endDate: format(addDays(new Date(priceStartDate), 1), 'yyyy-MM-dd'),
        });
      }
    }
  }, [priceStartDate, priceEndDate]);

  const handleWidgetClick = (e) => {
    e.stopPropagation();

    if (withDatesWidget) {
      setIsDateDropdownVisible(true);
    }
  };
  const onCheckPrice = (e) => {
    e.stopPropagation();

    if (withDatesWidget && !isDateDropdownVisible) {
      return setIsDateDropdownVisible(true);
    }

    if (startValue && endValue) {
      startTransition(() => {
        pushCheckAvailability(details);
        setShowError(false);
        onUpdateQueryDates({
          startDate: format(new Date(startValue), 'yyyy-MM-dd'),
          endDate: format(new Date(endValue), 'yyyy-MM-dd'),
        });
      });
    } else {
      if (dateRef.current) {
        setTimeout(() => setIsDateDropdownVisible(true));
      }
    }
  };

  useEffect(() => {
    document.body.classList.add(styles.with_footer);
    return () => document.body.classList.remove(styles.with_footer);
  }, []);

  const hasError =
    startValue &&
    endValue &&
    priceData &&
    showError &&
    !priceData?.showListingPrice?.price?.regular;
  const hasPrice = hasDates && price?.regular && !!price?.regular?.totalPrice;
  const hasDiscount =
    hasPrice && price?.discount?.totalPrice < price?.regular?.totalPrice;
  const hasDiscountTitle =
    hasDiscount && getHasDiscountTitle(hasDiscount, price);
  const hasFee = hasPrice && !!price?.cleaningFee;
  const isMonth = price?.unit === 'month';
  const currencyTranslation = t(`${price?.currency}`, price?.currency || '');
  const feeValue = getFormatValue(price?.cleaningFee, currencyTranslation);
  const titleMoneyPerUnitValue = getFormatValue(price?.regular?.price, ' ');
  const titleMoneyPerUnitDiscountValue = getFormatValue(
    price?.discount?.price,
    currencyTranslation
  );
  const totalMoneyValue = getFormatValue(
    price?.regular?.totalPrice,
    currencyTranslation
  );
  const discountedMoneyValue = getFormatValue(
    price?.discount?.totalPrice,
    currencyTranslation
  );
  const accomodationMoneyValue = getFormatValue(
    price?.regular?.totalPrice - price?.cleaningFee,
    currencyTranslation
  );
  const accomodationDiscountedMoneyValue = getFormatValue(
    price?.discount?.totalPrice - price?.cleaningFee,
    currencyTranslation
  );
  const discountedValue = getFormatValue(
    price?.discount?.totalPrice - price?.regular?.totalPrice,
    currencyTranslation
  );
  const titleMoneyPerDayValue = getTitleMoneyPerDayValue(
    hasDiscount ? price?.regular : price?.discount,
    price?.cleaningFee || 0,
    daysAmount,
    currencyTranslation
  );

  const onCanUpdate = useCallback(
    () => hasPrice && !(withDatesWidget && isDateDropdownVisible),
    [hasPrice, isDateDropdownVisible, withDatesWidget]
  );

  useWatchDateQuery({
    startValue,
    endValue,
    onCanUpdate,
    startTransition,
  });

  const guestsLimit = details.occupancy?.numGuests;
  const { guests, onIncrement, onDecrement } = useGuestsState(guestsLimit);
  const guestsCount = guests.adults + guests.children + guests.infants;
  const isPetsAllowed = details.occupancy?.petsAllowed;
  const { pets, onTogglePets } = usePetsState(isPetsAllowed);

  return (
    <>
      <section className={styles.container} onClick={handleWidgetClick}>
        <header>
          {hasPrice ? (
            <div className="text-slate-800 max-sm:text-base text-lg max-md:py-3">
              <>
                <span className="text-slate-800">
                  {hasDiscountTitle && (
                    <span className="text-[#429B94] font-bold line-through">
                      {titleMoneyPerUnitValue}
                    </span>
                  )}
                  <span
                    className={`${hasDiscountTitle && 'ml-1'} font-bold mr-1`}
                  >
                    {titleMoneyPerUnitDiscountValue}
                  </span>
                  <span className="font-medium">{t(price?.unit)}</span>
                </span>
              </>
              <div className="text-sm/[14px] mt-2 hidden max-sm:!block">
                <span className="font-medium">
                  {showShortsDateRange({
                    startDate: startValue,
                    endDate: endValue,
                  })}
                </span>
                <>
                  <span className=" font-semibold text-slate-800 ml-2 underline">
                    <span>{discountedMoneyValue} </span>
                    <span>{t('total')}</span>
                  </span>
                </>
              </div>
              <div className="mt-1 md:hidden">
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setPaymentSchedule(true);
                  }}
                  className="link link-sm link-primary link-bold"
                >
                  {t('apartment.payment-schedule.button.show')}
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="text-slate-800 max-sm:text-base text-lg max-md:py-3 max-sm:!hidden">
                <span className="font-medium">
                  {t('apartment.price.title')}{' '}
                </span>
              </div>
              <div className="ext-slate-800 max-sm:text-base text-lg max-md:py-3 hidden max-sm:!block">
                {hasError ? (
                  <>
                    <div className="font-medium">
                      {showShortsDateRange({
                        startDate: startValue,
                        endDate: endValue,
                      })}
                    </div>
                    <span
                      className="font-medium"
                      data-testid="apartment-no-price-mobile"
                    >
                      {t('apartment.price.hint')}
                    </span>
                  </>
                ) : (
                  <span className="font-medium">
                    {t('apartment.price.title')}{' '}
                  </span>
                )}
              </div>
            </>
          )}
          {hasPrice && price?.paymentSchedule?.length ? (
            <div className="float-right max-sm:!hidden">
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setPaymentSchedule(true);
                }}
                className="link link-lg link-primary"
              >
                {t('apartment.payment-schedule.button.show')}
              </span>
            </div>
          ) : null}
          {hasPrice && (
            <PaymentScheduleModal
              price={price}
              isMonth={isMonth}
              currencyTranslation={currencyTranslation}
              show={showPaymentSchedule}
              close={() => setPaymentSchedule(false)}
            />
          )}
        </header>

        <section className={`${styles.table} max-sm:!hidden`}>
          <div
            onClick={() => setIsDateDropdownVisible(true)}
            className={styles.row}
            ref={dateRef}
          >
            <button
              type="button"
              data-testid="availability-widget-todate-button"
            >
              <i className="icon-date_range text-primary-600 text-[24px] leading-none max-md:hidden" />
              <span className="flex flex-col items-start">
                <label>{t('check-in')}</label>
                <span
                  className="text-slate-800 text-sm font-medium mt-[-2px] mb-[-6px] inline-block truncate"
                  data-testid="availability-widget-fromdate"
                  data-testdate={startValue}
                >
                  {showFullDate(startValue, locale) || t('add-dates')}
                </span>
              </span>
            </button>

            <div className="w-[1px] min-w-[1px] h-full bg-slate-200 mx-[10px] py-[22px]" />

            <button
              type="button"
              data-testid="availability-widget-fromdate-button"
            >
              <i className="icon-date_range text-primary-600 text-[24px] leading-none max-md:hidden" />
              <span className="flex flex-col items-start">
                <label>{t('check-out')}</label>
                <span
                  className="text-slate-800 text-sm font-medium mt-[-2px] mb-[-6px] inline-block truncate"
                  data-testid="availability-widget-todate"
                  data-testdate={endValue}
                >
                  {showFullDate(endValue, locale) || t('add-dates')}
                </span>
              </span>
            </button>

            {isDateDropdownVisible &&
              (withDatesWidget ? (
                <DatesWidget
                  setIsDateDropdownVisible={setIsDateDropdownVisible}
                  date={{
                    startDate: startValue || null,
                    endDate: endValue || null,
                  }}
                  disabledDay={disabledDay}
                  disabledDates={disabledDates}
                  getMinimumDays={getMinimumDays}
                  getDaysRequirement={getDaysRequirement}
                  minDate={minDate}
                  maxDate={maxDate}
                  onDateChange={onChange}
                  onCheckPrice={onCheckPrice}
                />
              ) : (
                <DefaultDropdown
                  className={`date-range-dropdown-listing-details ${styles.daterange_dropdown} ${styles.daterange_dropdown_listing_details}`}
                  dataTestid="availability-widget-dropdown"
                >
                  <DatePicker
                    value={{
                      startDate: startValue || null,
                      endDate: endValue || null,
                    }}
                    onChange={onChange}
                    disabledDay={disabledDay}
                    disabledDates={disabledDates}
                    getMinimumDays={getMinimumDays}
                    getDaysRequirement={getDaysRequirement}
                    minDate={minDate}
                    maxDate={maxDate}
                    isFooterHidden
                  />
                </DefaultDropdown>
              ))}
          </div>

          <GuestsWidget
            guests={guests}
            onIncrement={onIncrement}
            onDecrement={onDecrement}
            guestsLimit={guestsLimit}
            pets={pets}
            onTogglePets={onTogglePets}
            isPetsAllowed={isPetsAllowed}
          />
        </section>

        {hasPrice ? (
          <ReserveControl
            startValue={startValue}
            endValue={endValue}
            guestsCount={guestsCount}
            details={details}
            priceLoading={loading || priceLoading}
          />
        ) : (
          <div>
            <div className="max-md:py-3">
              <button
                className="btn btn-primary btn-lg w-full relative"
                onClick={onCheckPrice}
                data-testid="apartment-check-availability-button"
              >
                {loading || priceLoading ? (
                  <Loader />
                ) : (
                  <span>{t('apartment.price.links.check')}</span>
                )}
              </button>
            </div>
            {priceError && (
              <div className="text-center text-primary-700 text-base leading-normal max-md:hidden mt-4">
                {priceError?.message || 'Server Error'}
              </div>
            )}
            {hasError ? (
              <div
                className="text-center text-primary-700 text-base leading-normal max-md:hidden mt-4"
                data-testid="apartment-no-price"
              >
                {t('apartment.price.hint')}
              </div>
            ) : null}
          </div>
        )}

        {hasPrice && (
          <section className={styles.details}>
            {isMonth ? (
              <div>
                <span>{t('accommodation')}</span>
                <b className="font-semibold">
                  {hasDiscount
                    ? accomodationMoneyValue
                    : accomodationDiscountedMoneyValue}
                </b>
              </div>
            ) : (
              <div>
                <span>
                  {titleMoneyPerDayValue} {daysAmount === 7 ? t('night') : null}{' '}
                  x {daysAmount} {daysAmount <= 1 ? t('night') : t('nights')}{' '}
                </span>
                <b className="font-semibold">
                  {hasDiscount
                    ? accomodationMoneyValue
                    : accomodationDiscountedMoneyValue}
                </b>
              </div>
            )}
            {hasDiscount && (
              <div className={styles.discount_wrap}>
                <span className={styles.discount}>
                  {t(`apartment.price.discountType.${price?.discount?.type}`)}
                </span>
                <b className="font-semibold">{discountedValue}</b>
              </div>
            )}
            {hasFee && (
              <div>
                <span className={styles.discount}>
                  {t('apartment.price.fee')}
                </span>
                <b className="font-semibold">{feeValue}</b>
              </div>
            )}
          </section>
        )}
        {hasPrice && totalMoneyValue && (
          <footer className="font-semibold">
            <span>{t('totalAmount')}</span>
            <span>{discountedMoneyValue}</span>
          </footer>
        )}
      </section>
    </>
  );
};

function ReserveControl({
  startValue,
  endValue,
  guestsCount,
  details,
  priceLoading,
}) {
  const t = useTranslations();
  const { push } = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const [reserveCreate, { loading, error }] = useMutation(reservationCreate);
  const onReserve = (e) => {
    e.stopPropagation();
    pushBookingCheckoutStart(details);
    reserveCreate({
      variables: {
        input: {
          reservationInput: {
            listingSlug: params.slug,
            startDate: new Date(startValue).toISOString(),
            endDate: new Date(endValue).toISOString(),
            numGuests: guestsCount,
          },
          successUrl: `${window.location.origin}/listings/${params.slug}/booking/success`,
          cancelUrl: `${window.location.origin}${pathname}`,
        },
      },
    })
      .then(({ data }) => {
        if (data?.reservationCreate?.checkoutUrl) {
          push(data?.reservationCreate?.checkoutUrl);
        }
      })
      .catch(() => {});
  };

  return (
    <div>
      <div className="max-md:py-3">
        <button
          className="btn btn-primary btn-lg max-sm:p-4 w-full relative"
          onClick={onReserve}
          data-testid="apartment-book-button"
        >
          {priceLoading || loading ? (
            <Loader />
          ) : (
            <span
              dangerouslySetInnerHTML={{
                __html: t.raw('apartment.price.links.reserve'),
              }}
            />
          )}
        </button>
      </div>
      {error && (
        <div className="text-center text-primary-700 text-base leading-normal max-md:hidden mt-4">
          {error?.message || 'Server Error'}
        </div>
      )}
      <div
        className="text-center text-primary-700 text-base leading-normal max-md:hidden mt-4"
        data-testid="apartment-wont-charge"
      >
        {t('apartment.price.payment')}
      </div>
    </div>
  );
}

function useGuestsState(limit) {
  const searchParams = useSearchParams();
  const [guests, setGuests] = useState(
    makeGuestsValidWithLimit(
      {
        adults: searchParams.getAll('adults'),
        infants: searchParams.getAll('infants'),
        children: searchParams.getAll('children'),
      },
      limit
    )
  );
  const onIncrement = useCallback(
    (type) => {
      setGuests(
        makeGuestsValidWithLimit({
          ...guests,
          [type]: guests[type] + 1,
        })
      );
    },
    [guests]
  );
  const onDecrement = useCallback(
    (type) => {
      setGuests(
        makeGuestsValidWithLimit({
          ...guests,
          [type]: guests[type] - 1,
        })
      );
    },
    [guests]
  );
  return {
    guests,
    onIncrement,
    onDecrement,
  };
}

function usePetsState(isAllowed) {
  const searchParams = useSearchParams();

  const [pets, setPets] = useState(
    isAllowed ? searchParams.get('pets') === 'enabled' : false
  );
  const onTogglePets = useCallback(
    (value) => {
      if (isAllowed) setPets(value ?? !pets);
    },
    [pets]
  );
  return {
    pets,
    onTogglePets,
  };
}
